import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  STRINGS,
  REVIEW_PLATFORMS,
  REVIEW_CHANNELS,
  REVIEW_TONES,
} from '@maintenance-tracker/shared';
import { PageHeader } from '@/components/ui/page-header';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input, Select } from '@/components/ui/input';
import { NoticeCard } from '@/components/ui/notice-card';
import { StatusBadge } from '@/components/ui/status-badge';
import { api, ApiError } from '@/lib/utils';
import { useAuth } from '@/lib/auth';
import {
  downloadBlob,
  printQrCard,
  qrPngBlob,
  qrSvg,
  QR_CARD_SIZES,
  type QrCardDesign,
  type QrCardSize,
} from '@/lib/qr';
import {
  CheckCircle2,
  Copy,
  ExternalLink,
  Info,
  QrCode,
  Share2,
  Sparkles,
  Star,
  FolderKanban,
  LayoutTemplate,
} from 'lucide-react';

type ToolId = 'ask' | 'qr' | 'projects' | 'proof' | 'referral';

type Summary = {
  destination: {
    id: string;
    platform: string;
    url: string;
    shortCode: string;
    clickCount: number;
  } | null;
  sentThisMonth: number;
  projectCounts: { total: number; ready: number; draft: number };
  testimonialCounts: { pending: number; approved: number };
  proofOutputCount: number;
  referralCount: number;
  settings: {
    weeklyReminderEnabled?: boolean;
    weeklyReminderWeekday?: number;
  };
  testimonialIntakeCode: string | null;
};

type Project = {
  id: string;
  title: string;
  service: string | null;
  status: string;
  consent: string | null;
  description: string | null;
  cityState: string | null;
  completedMonth: string | null;
};

type Testimonial = {
  id: string;
  body: string;
  displayName: string | null;
  source: string | null;
  sourceUrl: string | null;
  status: string;
};

const TOOLS: Array<{ id: ToolId; title: string; blurb: string; icon: typeof Star }> = [
  { id: 'ask', title: 'Ask for a review', blurb: 'Neutral messages you copy and send', icon: Star },
  { id: 'qr', title: 'Review QR card', blurb: 'Printable card with a short redirect', icon: QrCode },
  { id: 'projects', title: 'Project showcase', blurb: 'Photos with customer consent', icon: FolderKanban },
  { id: 'proof', title: 'Proof section', blurb: 'Embed or host consented work', icon: LayoutTemplate },
  { id: 'referral', title: 'Referral & share', blurb: 'Honest share kit for your channels', icon: Share2 },
];

const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

export function ReviewsProofPage() {
  const { user } = useAuth();
  const [tool, setTool] = useState<ToolId | null>(null);
  const [summary, setSummary] = useState<Summary | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await api<Summary>('/api/v1/proof/summary');
      setSummary(data);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not load Proof Kit');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const statusFor = (id: ToolId): string => {
    if (!summary) return '—';
    if (id === 'ask') return summary.destination ? `${summary.sentThisMonth} marked sent this month` : 'Set up destination';
    if (id === 'qr') return summary.destination ? 'Ready' : 'Needs destination';
    if (id === 'projects') return `${summary.projectCounts.ready} ready · ${summary.projectCounts.draft} draft`;
    if (id === 'proof') return summary.proofOutputCount ? `${summary.proofOutputCount} output(s)` : 'Not built yet';
    return summary.referralCount ? `${summary.referralCount} saved` : 'Not set up';
  };

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <PageHeader
        title={STRINGS.proof.sectionTitle}
        description={STRINGS.proof.sectionSubtitle}
      />

      {error && (
        <p className="rounded-lg border border-danger/30 bg-danger-subtle px-3 py-2 text-sm text-danger">{error}</p>
      )}

      {!tool && (
        <>
          <div className="grid gap-3 sm:grid-cols-2">
            {TOOLS.map((t) => {
              const Icon = t.icon;
              return (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => setTool(t.id)}
                  className="rounded-xl border border-border bg-bg-surface p-4 text-left shadow-card transition hover:border-brand/30 hover:shadow-raised"
                >
                  <div className="flex items-start gap-3">
                    <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-brand-subtle text-brand">
                      <Icon className="h-4 w-4" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-[15px] font-semibold text-text-primary">{t.title}</p>
                      <p className="mt-0.5 text-[13px] text-text-secondary">{t.blurb}</p>
                      <p className="mt-2 text-xs text-text-muted">{loading ? '…' : statusFor(t.id)}</p>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>

          <NoticeCard icon={Sparkles} title="Coming soon">
            <p>
              {STRINGS.proof.socialComingSoon}{' '}
              <a href="/app/social" className="font-medium text-brand hover:underline">
                Open Social
              </a>
            </p>
          </NoticeCard>
        </>
      )}

      {tool && (
        <div className="space-y-4">
          <Button variant="ghost" size="sm" onClick={() => setTool(null)}>
            ← All tools
          </Button>
          {tool === 'ask' && (
            <AskTool
              summary={summary}
              businessName={user?.workspace?.name || 'our team'}
              onChanged={refresh}
            />
          )}
          {tool === 'qr' && <QrTool summary={summary} businessName={user?.workspace?.name || 'Business'} />}
          {tool === 'projects' && <ProjectsTool onChanged={refresh} />}
          {tool === 'proof' && <ProofOutputTool summary={summary} onChanged={refresh} />}
          {tool === 'referral' && <ReferralTool onChanged={refresh} />}
        </div>
      )}
    </div>
  );
}

function AskTool({
  summary,
  businessName,
  onChanged,
}: {
  summary: Summary | null;
  businessName: string;
  onChanged: () => void;
}) {
  const [platform, setPlatform] = useState('google');
  const [url, setUrl] = useState(summary?.destination?.url || '');
  const [channel, setChannel] = useState<(typeof REVIEW_CHANNELS)[number]>('text');
  const [tone, setTone] = useState<(typeof REVIEW_TONES)[number]>('friendly');
  const [customerFirstName, setCustomerFirstName] = useState('');
  const [jobType, setJobType] = useState('');
  const [personalLine, setPersonalLine] = useState('');
  const [variants, setVariants] = useState<
    Array<{ id: string; label: string; body: string; smsHref: string | null; mailtoHref: string | null }>
  >([]);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [weekday, setWeekday] = useState(summary?.settings.weeklyReminderWeekday ?? 1);
  const [reminderOn, setReminderOn] = useState(Boolean(summary?.settings.weeklyReminderEnabled));

  useEffect(() => {
    if (summary?.destination) {
      setUrl(summary.destination.url);
      setPlatform(summary.destination.platform);
    }
  }, [summary]);

  async function saveDestination() {
    setBusy(true);
    setMsg(null);
    try {
      await api('/api/v1/proof/destinations', {
        method: 'PUT',
        body: JSON.stringify({ platform, url }),
      });
      setMsg('Destination saved.');
      onChanged();
    } catch (e) {
      setMsg(e instanceof ApiError ? e.message : 'Save failed');
    } finally {
      setBusy(false);
    }
  }

  async function generate() {
    setBusy(true);
    setMsg(null);
    try {
      const res = await api<{
        variants: typeof variants;
        honestyNotice: string;
      }>('/api/v1/proof/messages/generate', {
        method: 'POST',
        body: JSON.stringify({
          channel,
          tone,
          customerFirstName: customerFirstName || undefined,
          jobType: jobType || undefined,
          personalLine: personalLine || undefined,
        }),
      });
      setVariants(res.variants);
      setMsg(res.honestyNotice);
    } catch (e) {
      setMsg(e instanceof ApiError ? e.message : 'Generate failed');
    } finally {
      setBusy(false);
    }
  }

  async function markSent() {
    await api('/api/v1/proof/messages/mark-sent', {
      method: 'POST',
      body: JSON.stringify({ channel }),
    });
    setMsg(STRINGS.proof.reviewRequestCopied);
    onChanged();
  }

  async function saveReminder() {
    await api('/api/v1/proof/settings', {
      method: 'PATCH',
      body: JSON.stringify({ weeklyReminderEnabled: reminderOn, weeklyReminderWeekday: weekday }),
    });
    setMsg('Reminder preference saved. (Weekly email ships with notifications.)');
    onChanged();
  }

  return (
    <div className="space-y-4">
      <Card title="Review destination" description="Paste your review link. We never edit it.">
        <div className="space-y-3">
          <Select
            label="Platform"
            value={platform}
            onChange={(e) => setPlatform(e.target.value)}
          >
            {REVIEW_PLATFORMS.filter((p) => p !== 'testimonial_page').map((p) => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </Select>
          <Input label="Review URL" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://…" />
          <div className="flex flex-wrap gap-2">
            <Button loading={busy} onClick={() => void saveDestination()}>
              Save destination
            </Button>
            {url && (
              <a href={url} target="_blank" rel="noopener noreferrer">
                <Button variant="secondary" type="button">
                  Test link <ExternalLink className="h-3.5 w-3.5" />
                </Button>
              </a>
            )}
          </div>
          <p className="text-xs text-text-muted">
            How to find your Google review link: open Google Business Profile → Ask for reviews → copy the short link.
          </p>
          {summary?.testimonialIntakeCode && (
            <p className="text-xs text-text-secondary">
              Or ask for a testimonial on your own page:{' '}
              <code className="rounded bg-bg-subtle px-1">/t/{summary.testimonialIntakeCode}</code>
            </p>
          )}
        </div>
      </Card>

      <Card title="Create a message" description={STRINGS.proof.honestyNotice}>
        <div className="grid gap-3 sm:grid-cols-2">
          <Select label="Channel" value={channel} onChange={(e) => setChannel(e.target.value as typeof channel)}>
            {REVIEW_CHANNELS.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </Select>
          <Select label="Tone" value={tone} onChange={(e) => setTone(e.target.value as typeof tone)}>
            {REVIEW_TONES.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </Select>
          <Input label="Customer first name" value={customerFirstName} onChange={(e) => setCustomerFirstName(e.target.value)} />
          <Input label="Job type" value={jobType} onChange={(e) => setJobType(e.target.value)} />
        </div>
        <div className="mt-3">
          <Input
            label="Personal line (optional)"
            value={personalLine}
            onChange={(e) => setPersonalLine(e.target.value)}
            placeholder="Thanks again for trusting us with your kitchen."
          />
        </div>
        <Button className="mt-4" loading={busy} onClick={() => void generate()} disabled={!summary?.destination}>
          Generate messages
        </Button>
        <p className="mt-2 text-xs text-text-muted">Using short link for {businessName}.</p>
      </Card>

      {variants.map((v) => (
        <Card key={v.id} title={v.label}>
          <pre className="whitespace-pre-wrap rounded-lg bg-bg-subtle p-3 text-sm text-text-primary">{v.body}</pre>
          <div className="mt-3 flex flex-wrap gap-2">
            <Button
              size="sm"
              variant="secondary"
              onClick={() => {
                void navigator.clipboard.writeText(v.body);
                setMsg(STRINGS.proof.reviewRequestCopied);
              }}
            >
              <Copy className="h-3.5 w-3.5" /> Copy
            </Button>
            {v.smsHref && (
              <a href={v.smsHref}>
                <Button size="sm" variant="secondary">
                  Open in Messages
                </Button>
              </a>
            )}
            {v.mailtoHref && (
              <a href={v.mailtoHref}>
                <Button size="sm" variant="secondary">
                  Open in Mail
                </Button>
              </a>
            )}
            <Button size="sm" onClick={() => void markSent()}>
              Mark as sent
            </Button>
          </div>
        </Card>
      ))}

      <Card title="After-job reminder" description="We email you a weekly nudge — we never message your customers.">
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={reminderOn} onChange={(e) => setReminderOn(e.target.checked)} />
          Remind me to ask after each job
        </label>
        <div className="mt-3 max-w-xs">
          <Select label="Weekday" value={String(weekday)} onChange={(e) => setWeekday(Number(e.target.value))}>
            {WEEKDAYS.map((d, i) => (
              <option key={d} value={i}>
                {d}
              </option>
            ))}
          </Select>
        </div>
        <Button className="mt-3" size="sm" variant="secondary" onClick={() => void saveReminder()}>
          Save reminder
        </Button>
      </Card>

      {msg && <p className="text-sm text-text-secondary">{msg}</p>}
    </div>
  );
}

function QrTool({ summary, businessName }: { summary: Summary | null; businessName: string }) {
  const [design, setDesign] = useState<QrCardDesign>('clean');
  const [size, setSize] = useState<QrCardSize>('business_card');
  const [line, setLine] = useState('Scan to leave an honest review');
  const [phone, setPhone] = useState('');
  const dest = summary?.destination;
  const goUrl = dest ? `${window.location.origin}/go/${dest.shortCode}` : '';

  const svg = useMemo(() => {
    if (!goUrl) return '';
    const dark = design === 'dark' ? '#f3f5fb' : design === 'accent' ? '#2b54e4' : '#0b1120';
    const light = design === 'dark' ? '#0a0e17' : '#ffffff';
    return qrSvg(goUrl, { dark, light });
  }, [goUrl, design]);

  if (!dest) {
    return (
      <Card title="Review QR card">
        <p className="text-sm text-text-secondary">Set up a review destination in “Ask for a review” first.</p>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <NoticeCard icon={Info} title="About this link">
        <p>{STRINGS.proof.qrTrackingNote}</p>
        <p className="mt-1">{STRINGS.proof.qrOptionalClicks} Current opens: {dest.clickCount}</p>
      </NoticeCard>

      <Card title="Card preview">
        <div className="grid gap-3 sm:grid-cols-2">
          <Select label="Design" value={design} onChange={(e) => setDesign(e.target.value as QrCardDesign)}>
            <option value="clean">Clean white</option>
            <option value="dark">Dark</option>
            <option value="accent">Trade-colored accent</option>
          </Select>
          <Select label="Size" value={size} onChange={(e) => setSize(e.target.value as QrCardSize)}>
            {(Object.keys(QR_CARD_SIZES) as QrCardSize[]).map((k) => (
              <option key={k} value={k}>
                {QR_CARD_SIZES[k].label}
              </option>
            ))}
          </Select>
          <Input label="Short line" value={line} onChange={(e) => setLine(e.target.value)} />
          <Input label="Phone (optional)" value={phone} onChange={(e) => setPhone(e.target.value)} />
        </div>
        <div
          className={`mt-4 flex flex-col items-center gap-2 rounded-xl border p-6 ${
            design === 'dark' ? 'border-ink-line bg-ink text-ink-fg' : 'border-border bg-bg-surface'
          }`}
        >
          <p className={`text-base font-semibold ${design === 'accent' ? 'text-brand' : ''}`}>{businessName}</p>
          <p className="text-sm text-text-secondary">{line}</p>
          <div className="w-40" dangerouslySetInnerHTML={{ __html: svg }} />
          {phone && <p className="text-sm">{phone}</p>}
          <p className="text-xs text-text-muted break-all">{goUrl}</p>
        </div>
        <p className="mt-2 text-xs text-text-muted">Tip: test scan with your phone camera.</p>
        <div className="mt-4 flex flex-wrap gap-2">
          <Button
            size="sm"
            variant="secondary"
            onClick={() => {
              downloadBlob(new Blob([svg], { type: 'image/svg+xml' }), 'review-qr.svg');
            }}
          >
            Download SVG
          </Button>
          <Button
            size="sm"
            variant="secondary"
            onClick={() => {
              void qrPngBlob(goUrl, 1024).then((b) => downloadBlob(b, 'review-qr.png'));
            }}
          >
            Download PNG
          </Button>
          <Button
            size="sm"
            onClick={() =>
              printQrCard({ svg, businessName, line, phone, size, design })
            }
          >
            Print / PDF
          </Button>
        </div>
      </Card>
    </div>
  );
}

function ProjectsTool({ onChanged }: { onChanged: () => void }) {
  const [projects, setProjects] = useState<Project[]>([]);
  const [testimonials, setTestimonials] = useState<Testimonial[]>([]);
  const [intakeUrl, setIntakeUrl] = useState('');
  const [title, setTitle] = useState('');
  const [service, setService] = useState('');
  const [description, setDescription] = useState('');
  const [photosPublic, setPhotosPublic] = useState(false);
  const [method, setMethod] = useState('verbal');
  const [showLocation, setShowLocation] = useState(false);
  const [namedQuoted, setNamedQuoted] = useState(false);
  const [pasteBody, setPasteBody] = useState('');
  const [pasteSource, setPasteSource] = useState('');
  const [pasteUrl, setPasteUrl] = useState('');
  const [msg, setMsg] = useState<string | null>(null);

  const load = useCallback(async () => {
    const [p, t] = await Promise.all([
      api<{ projects: Project[] }>('/api/v1/proof/projects'),
      api<{ testimonials: Testimonial[]; intakeUrl: string }>('/api/v1/proof/testimonials'),
    ]);
    setProjects(p.projects);
    setTestimonials(t.testimonials);
    setIntakeUrl(t.intakeUrl);
  }, []);

  useEffect(() => {
    void load().catch((e) => setMsg(e instanceof ApiError ? e.message : 'Load failed'));
  }, [load]);

  async function create() {
    try {
      await api('/api/v1/proof/projects', {
        method: 'POST',
        body: JSON.stringify({
          title,
          service: service || null,
          description: description || null,
          status: photosPublic ? 'ready' : 'draft',
          consent: { photosPublic, method, showLocation, namedQuoted },
        }),
      });
      setTitle('');
      setDescription('');
      setMsg('Project saved.');
      await load();
      onChanged();
    } catch (e) {
      setMsg(e instanceof ApiError ? e.message : 'Could not save project');
    }
  }

  async function revoke(id: string) {
    const res = await api<{ message: string }>(`/api/v1/proof/projects/${id}/revoke-consent`, {
      method: 'POST',
    });
    setMsg(res.message);
    await load();
    onChanged();
  }

  async function eConsent(id: string) {
    const res = await api<{ url: string }>(`/api/v1/proof/projects/${id}/e-consent-link`, {
      method: 'POST',
    });
    await navigator.clipboard.writeText(res.url);
    setMsg(`E-consent link copied: ${res.url}`);
  }

  async function pasteReview() {
    try {
      await api('/api/v1/proof/testimonials/paste', {
        method: 'POST',
        body: JSON.stringify({
          body: pasteBody,
          source: pasteSource,
          sourceUrl: pasteUrl,
        }),
      });
      setPasteBody('');
      setMsg('Pasted review saved (owner-provided, original words kept).');
      await load();
      onChanged();
    } catch (e) {
      setMsg(e instanceof ApiError ? e.message : 'Paste failed');
    }
  }

  return (
    <div className="space-y-4">
      <NoticeCard icon={Info} title="Photo reminder">
        <p>{STRINGS.proof.photoReminder}</p>
      </NoticeCard>

      <Card title="Add a project">
        <div className="space-y-3">
          <Input label="Title" required value={title} onChange={(e) => setTitle(e.target.value)} />
          <Input label="Service type" value={service} onChange={(e) => setService(e.target.value)} />
          <label className="block text-[13px] font-medium">
            Description
            <textarea
              className="mt-1.5 h-24 w-full rounded-lg border border-border-strong bg-bg-surface px-3 py-2 text-sm"
              maxLength={600}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What was the job and what did you do?"
            />
          </label>
          <fieldset className="space-y-2 rounded-lg border border-border p-3 text-sm">
            <legend className="px-1 text-[13px] font-semibold">Consent (required for Ready)</legend>
            <label className="flex gap-2">
              <input type="checkbox" checked={photosPublic} onChange={(e) => setPhotosPublic(e.target.checked)} />
              Customer agreed photos can be shown publicly
            </label>
            <Select label="How they agreed" value={method} onChange={(e) => setMethod(e.target.value)}>
              <option value="verbal">Verbal</option>
              <option value="written">Written / text</option>
              <option value="signed">Signed form</option>
            </Select>
            <label className="flex gap-2">
              <input type="checkbox" checked={showLocation} onChange={(e) => setShowLocation(e.target.checked)} />
              May show general location
            </label>
            <label className="flex gap-2">
              <input type="checkbox" checked={namedQuoted} onChange={(e) => setNamedQuoted(e.target.checked)} />
              Customer agreed to be named / quoted
            </label>
          </fieldset>
          <Button onClick={() => void create()} disabled={!title.trim()}>
            Save project
          </Button>
        </div>
      </Card>

      <Card title="Projects">
        <ul className="space-y-3">
          {projects.map((p) => (
            <li key={p.id} className="rounded-lg border border-border p-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                  <p className="font-medium">{p.title}</p>
                  <p className="text-xs text-text-muted">{p.service || '—'}</p>
                </div>
                <StatusBadge status={p.status === 'ready' ? 'active' : p.status === 'hidden' ? 'paused' : 'draft'} />
              </div>
              <div className="mt-2 flex flex-wrap gap-2">
                <Button size="sm" variant="secondary" onClick={() => void eConsent(p.id)}>
                  Copy e-consent link
                </Button>
                <a href={`/api/v1/proof/projects/${p.id}/consent-pdf.txt`}>
                  <Button size="sm" variant="secondary">
                    Consent form
                  </Button>
                </a>
                <Button size="sm" variant="destructive-outline" onClick={() => void revoke(p.id)}>
                  Revoke consent
                </Button>
              </div>
            </li>
          ))}
          {!projects.length && <p className="text-sm text-text-secondary">No projects yet.</p>}
        </ul>
      </Card>

      <Card title="Testimonials" description="We never edit customer words. Pasted reviews need a source and link.">
        <p className="mb-3 text-sm text-text-secondary">
          Customer request link:{' '}
          <button
            type="button"
            className="font-medium text-brand"
            onClick={() => void navigator.clipboard.writeText(intakeUrl)}
          >
            {intakeUrl || '…'}
          </button>
        </p>
        <div className="space-y-2 border-t border-border pt-3">
          <p className="text-[13px] font-medium">Paste a review you already received</p>
          <textarea
            className="h-20 w-full rounded-lg border border-border-strong px-3 py-2 text-sm"
            maxLength={800}
            value={pasteBody}
            onChange={(e) => setPasteBody(e.target.value)}
            placeholder="Paste original words only"
          />
          <Input label="Source name" value={pasteSource} onChange={(e) => setPasteSource(e.target.value)} placeholder="Google review" />
          <Input label="Where is the original?" value={pasteUrl} onChange={(e) => setPasteUrl(e.target.value)} placeholder="https://…" />
          <Button size="sm" onClick={() => void pasteReview()}>
            Save pasted review
          </Button>
        </div>
        <ul className="mt-4 space-y-2">
          {testimonials.map((t) => (
            <li key={t.id} className="rounded-lg bg-bg-subtle p-3 text-sm">
              <p className="whitespace-pre-wrap">{t.body}</p>
              <p className="mt-1 text-xs text-text-muted">
                {t.displayName || 'Customer'} · {t.status}
                {t.sourceUrl ? ` · ${t.source}` : ''}
              </p>
              <div className="mt-2 flex gap-2">
                {t.status === 'pending' && (
                  <Button
                    size="sm"
                    onClick={() =>
                      void api(`/api/v1/proof/testimonials/${t.id}/status`, {
                        method: 'PATCH',
                        body: JSON.stringify({ status: 'approved' }),
                      }).then(load)
                    }
                  >
                    Approve
                  </Button>
                )}
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() =>
                    void api(`/api/v1/proof/testimonials/${t.id}/status`, {
                      method: 'PATCH',
                      body: JSON.stringify({ status: 'hidden' }),
                    }).then(load)
                  }
                >
                  Hide
                </Button>
                <Button
                  size="sm"
                  variant="destructive-outline"
                  onClick={() => void api(`/api/v1/proof/testimonials/${t.id}`, { method: 'DELETE' }).then(load)}
                >
                  Delete
                </Button>
              </div>
            </li>
          ))}
        </ul>
      </Card>
      {msg && <p className="text-sm text-text-secondary">{msg}</p>}
    </div>
  );
}

function ProofOutputTool({ summary, onChanged }: { summary: Summary | null; onChanged: () => void }) {
  const [projects, setProjects] = useState<Project[]>([]);
  const [testimonials, setTestimonials] = useState<Testimonial[]>([]);
  const [selected, setSelected] = useState<Array<{ type: 'project' | 'testimonial'; id: string }>>([]);
  const [layout, setLayout] = useState<'grid' | 'before_after' | 'testimonials_row'>('grid');
  const [enableHosted, setEnableHosted] = useState(true);
  const [result, setResult] = useState<{
    embedCode: string;
    hostedUrl: string | null;
    output: { id: string };
  } | null>(null);
  const [msg, setMsg] = useState<string | null>(null);

  useEffect(() => {
    void Promise.all([
      api<{ projects: Project[] }>('/api/v1/proof/projects?status=ready'),
      api<{ testimonials: Testimonial[] }>('/api/v1/proof/testimonials'),
    ]).then(([p, t]) => {
      setProjects(p.projects);
      setTestimonials(t.testimonials.filter((x) => x.status === 'approved'));
    });
  }, [summary]);

  function toggle(type: 'project' | 'testimonial', id: string) {
    setSelected((prev) => {
      const exists = prev.some((x) => x.type === type && x.id === id);
      if (exists) return prev.filter((x) => !(x.type === type && x.id === id));
      if (prev.length >= 12) return prev;
      return [...prev, { type, id }];
    });
  }

  async function build() {
    try {
      const res = await api<{
        embedCode: string;
        hostedUrl: string | null;
        output: { id: string };
      }>('/api/v1/proof/outputs', {
        method: 'POST',
        body: JSON.stringify({
          layout,
          selectedIds: selected,
          enableHosted,
          showOwnerProvidedFootnote: true,
          structuredDataEnabled: false,
        }),
      });
      setResult(res);
      setMsg('Proof output created. Non-consented items are never included.');
      onChanged();
    } catch (e) {
      setMsg(e instanceof ApiError ? e.message : 'Build failed');
    }
  }

  return (
    <div className="space-y-4">
      <Card title="Pick items" description="Only Ready projects with consent and approved testimonials.">
        <div className="space-y-2">
          {projects.map((p) => (
            <label key={p.id} className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={selected.some((s) => s.type === 'project' && s.id === p.id)}
                onChange={() => toggle('project', p.id)}
              />
              {p.title}
            </label>
          ))}
          {testimonials.map((t) => (
            <label key={t.id} className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={selected.some((s) => s.type === 'testimonial' && s.id === t.id)}
                onChange={() => toggle('testimonial', t.id)}
              />
              {(t.displayName || 'Customer') + ': ' + t.body.slice(0, 60)}
            </label>
          ))}
          {!projects.length && !testimonials.length && (
            <p className="text-sm text-text-secondary">Nothing ready yet — add consented projects or approve testimonials.</p>
          )}
        </div>
        <div className="mt-3 grid gap-3 sm:grid-cols-2">
          <Select label="Layout" value={layout} onChange={(e) => setLayout(e.target.value as typeof layout)}>
            <option value="grid">Project grid</option>
            <option value="before_after">Before/After list</option>
            <option value="testimonials_row">Testimonials + projects</option>
          </Select>
          <label className="flex items-end gap-2 pb-2 text-sm">
            <input type="checkbox" checked={enableHosted} onChange={(e) => setEnableHosted(e.target.checked)} />
            Hosted page /p/…
          </label>
        </div>
        <Button className="mt-3" onClick={() => void build()} disabled={!selected.length}>
          Build proof section
        </Button>
      </Card>

      {result && (
        <Card title="Outputs">
          <p className="text-sm text-text-secondary">Embed (script + iframe):</p>
          <pre className="mt-1 overflow-x-auto rounded-lg bg-bg-subtle p-3 text-xs">{result.embedCode}</pre>
          <Button
            size="sm"
            className="mt-2"
            variant="secondary"
            onClick={() => void navigator.clipboard.writeText(result.embedCode)}
          >
            Copy embed
          </Button>
          {result.hostedUrl && (
            <p className="mt-3 text-sm">
              Hosted:{' '}
              <a className="text-brand hover:underline" href={result.hostedUrl} target="_blank" rel="noreferrer">
                {result.hostedUrl}
              </a>
            </p>
          )}
          <a className="mt-3 inline-flex" href={`/api/v1/proof/outputs/${result.output.id}/html`}>
            <Button size="sm" variant="secondary">
              Download static HTML
            </Button>
          </a>
          <p className="mt-3 text-xs text-text-muted">
            Platform tips: paste the embed into WordPress Custom HTML, Wix Embed, Squarespace Code, GoDaddy website builder,
            or Webflow Embed. Or host the downloaded HTML yourself.
          </p>
        </Card>
      )}
      {msg && <p className="text-sm text-text-secondary">{msg}</p>}
    </div>
  );
}

function ReferralTool({ onChanged }: { onChanged: () => void }) {
  const [destinationUrl, setDestinationUrl] = useState('');
  const [message, setMessage] = useState(
    "If you know a homeowner who needs help, feel free to share my contact link. No pressure — just if it's useful.",
  );
  const [assets, setAssets] = useState<Array<{ id: string; destinationUrl: string; message: string | null }>>([]);
  const [note, setNote] = useState(STRINGS.proof.referralTrackingNote as string);

  useEffect(() => {
    void api<{ assets: typeof assets; trackingNote: string }>('/api/v1/proof/referral-assets').then((r) => {
      setAssets(r.assets);
      setNote(r.trackingNote);
    });
  }, []);

  async function save() {
    const res = await api<{ asset: (typeof assets)[0]; trackingNote: string }>('/api/v1/proof/referral-assets', {
      method: 'POST',
      body: JSON.stringify({ destinationUrl, message }),
    });
    setAssets((a) => [res.asset, ...a]);
    setNote(res.trackingNote);
    onChanged();
  }

  const combined = `${message}\n\n${destinationUrl}`.trim();

  return (
    <div className="space-y-4">
      <NoticeCard icon={CheckCircle2} title="Honest tracking">
        <p>{note}</p>
      </NoticeCard>
      <Card title="Referral share kit">
        <div className="space-y-3">
          <Input
            label="Referral destination URL"
            value={destinationUrl}
            onChange={(e) => setDestinationUrl(e.target.value)}
            placeholder="Your quote page or contact link"
          />
          <label className="block text-[13px] font-medium">
            Short message
            <textarea
              className="mt-1.5 h-24 w-full rounded-lg border border-border-strong px-3 py-2 text-sm"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
            />
          </label>
          <div className="flex flex-wrap gap-2">
            <Button onClick={() => void save()} disabled={!destinationUrl}>
              Save asset
            </Button>
            <Button
              variant="secondary"
              onClick={() => void navigator.clipboard.writeText(combined)}
              disabled={!destinationUrl}
            >
              Copy message + link
            </Button>
            {destinationUrl && (
              <Button
                variant="secondary"
                onClick={() => {
                  const svg = qrSvg(destinationUrl);
                  downloadBlob(new Blob([svg], { type: 'image/svg+xml' }), 'referral-qr.svg');
                }}
              >
                Download QR SVG
              </Button>
            )}
          </div>
        </div>
      </Card>
      {assets.length > 0 && (
        <Card title="Saved assets">
          <ul className="space-y-2 text-sm">
            {assets.map((a) => (
              <li key={a.id} className="rounded-lg border border-border p-3">
                <p className="break-all text-text-primary">{a.destinationUrl}</p>
                {a.message && <p className="mt-1 text-text-secondary">{a.message}</p>}
              </li>
            ))}
          </ul>
        </Card>
      )}
    </div>
  );
}
